<template>
  <transition name="cx-toast-fade">
    <div v-if="visible" class="cx-toast-overlay">
      <div class="cx-toast-container">
        <div class="cx-toast" :class="{ 'cx-toast--clickable': clickable }">
          <button
            v-if="closable"
            type="button"
            class="cx-toast__close"
            :aria-label="CX_TOAST_DISMISS_LABEL"
            @click.stop="$emit('close')"
          >
            <img :src="require('@/assets/chat-bot/close.svg')" width="16" height="16" alt="" />
          </button>
          <!-- Click semantics live here, a sibling of the close button, so the two
               controls are not nested for assistive tech. -->
          <div
            class="cx-toast__content"
            :role="clickable ? 'button' : null"
            :tabindex="clickable ? 0 : null"
            @click="clickable && $emit('click')"
            @keydown.enter.self="clickable && $emit('click')"
            @keydown.space.self.prevent="clickable && $emit('click')"
          >
            <slot />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { CX_TOAST_DISMISS_LABEL } from '@/constants/chat';

export default {
  name: 'CxToastCard',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    closable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { CX_TOAST_DISMISS_LABEL };
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/spacings';
@import '~@riseupil/base-ui/src/scss/riseup-z-index';

/* Pinned to the app frame's bottom edge, immune to whichever container
   scrolls. The offsets mirror ResponsivePage's frame CSS (5vh top margin;
   55px = footer + card margin) — if that frame math changes, change this. */
.cx-toast-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  pointer-events: none;
  z-index: $toast-message;
}

.cx-toast-container {
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  padding: 0 17px;
  margin-bottom: $ri-spacing-m;
  pointer-events: auto;

  @media (min-width: 460px) {
    margin-bottom: calc(100vh * 0.05 + 55px + 20px);
  }
}

/* Figma: intercom in RiseUp Chat, node 17092:76316. White rather than the
   in-chat cream because it sits over page content. */
.cx-toast {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  border: 1px solid rgba($riseup_black, 0.03);
  // Figma 17092:76316 geometry; no 20px radius / 4-12.5 shadow token exists.
  border-radius: 20px;
  background-color: $riseup_white;
  box-shadow: 0 4px 12.5px rgba($riseup_black, 0.19);
}

.cx-toast--clickable .cx-toast__content {
  cursor: pointer;
}

.cx-toast__content {
  width: 100%;
}

/* Figma puts the 16px lucide/x at 20,15 from the card's corner; the button box
   is larger for the finger, negative-margined so the icon stays on that spot.
   Slot content owns its own clearance from it (the toast reserves 40px left). */
.cx-toast__close {
  position: absolute;
  top: 15px;
  left: 20px;
  width: 32px;
  height: 32px;
  margin: -8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.55;
  transition: opacity 0.15s ease;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
}

.cx-toast-fade-enter-active,
.cx-toast-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.cx-toast-fade-enter,
.cx-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
